import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Wrench, Package, Plus } from "@phosphor-icons/react";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from "@/components/ui/drawer";

export const JobLinks = ({ jobId }) => {
  const [job, setJob] = useState(null);
  const [suggest, setSuggest] = useState(null);
  const [props, setProps] = useState([]);
  const [equip, setEquip] = useState([]);
  const [newProp, setNewProp] = useState(false);
  const [pForm, setPForm] = useState({ name: "", service_address: "", city: "", state: "", zip: "" });
  const [addEq, setAddEq] = useState(false);
  const [eForm, setEForm] = useState({ equipment_type: "", nickname: "", manufacturer: "", model: "", serial_number: "", physical_location: "" });
  const [useStock, setUseStock] = useState(false);
  const [stockList, setStockList] = useState([]);
  const [stockQ, setStockQ] = useState("");

  const load = async () => {
    const j = (await api.get(`/jobs/${jobId}`)).data;
    setJob(j);
    const ps = (await api.get("/properties")).data;
    setProps(ps);
    if (j.property_id) { setEquip((await api.get(`/equipment?property_id=${j.property_id}`)).data); setSuggest(null); }
    else if (j.location) {
      let cid = "";
      try { const cs = (await api.get(`/customers?q=${encodeURIComponent(j.customer_name || "")}`)).data; cid = cs[0]?.id || ""; } catch (e) { console.error("customer lookup failed:", e); }
      try {
        const m = (await api.post("/properties/match", { address: j.location, customer_id: cid })).data;
        const hit = (m.exact && m.exact[0]) || (m.partial && m.partial[0]);
        if (hit) setSuggest(hit);
      } catch (e) { console.error("property match failed:", e); }
    }
  };
  useEffect(() => { load(); }, [jobId]);

  const save = async (patch) => {
    const updated = { ...job, ...patch };
    setJob(updated);
    await api.put(`/jobs/${jobId}`, updated);
  };

  const setProperty = async (pid) => {
    await save({ property_id: pid });
    setEquip(pid ? (await api.get(`/equipment?property_id=${pid}`)).data : []);
  };

  const createProperty = async () => {
    if (!pForm.service_address) { toast.error("Enter a service address"); return; }
    let cid = "";
    try { const cs = (await api.get(`/customers?q=${encodeURIComponent(job.customer_name || "")}`)).data; cid = cs[0]?.id || ""; } catch (e) { console.error("customer lookup failed:", e); }
    const p = (await api.post("/properties", { ...pForm, customer_id: cid })).data;
    setProps((x) => [p, ...x.filter((y) => y.id !== p.id)]);
    setNewProp(false);
    setPForm({ name: "", service_address: "", city: "", state: "", zip: "" });
    await setProperty(p.id);
    toast.success("Property linked");
  };

  const createEquipment = async () => {
    if (!job.property_id) { toast.error("Select a property first"); return; }
    if (!eForm.nickname && !eForm.model) { toast.error("Add a nickname or model"); return; }
    const e = (await api.post("/equipment", { ...eForm, property_id: job.property_id })).data;
    setEquip((x) => [...x, e]);
    setAddEq(false);
    setEForm({ equipment_type: "", nickname: "", manufacturer: "", model: "", serial_number: "", physical_location: "" });
    toast.success("Equipment added");
  };

  const setTaskEquip = async (taskId, eid) => {
    const tasks = job.tasks.map((t) => (t.id === taskId ? { ...t, equipment_id: eid } : t));
    await save({ tasks });
  };

  const eqMatch = (t) => {
    if (t.equipment_id) return null;
    const d = (t.description || "").toLowerCase();
    return equip.find((e) => {
      const toks = `${e.nickname || ""} ${e.model || ""} ${e.manufacturer || ""} ${e.equipment_type || ""}`
        .toLowerCase().split(/\s+/).filter((w) => w.length > 2);
      return toks.some((w) => d.includes(w));
    });
  };

  const searchStock = async (q) => { setStockQ(q); setStockList((await api.get(`/stock?q=${encodeURIComponent(q)}`)).data); };
  const openStock = async () => { setUseStock(true); setStockList((await api.get("/stock")).data); };
  const doUse = async (item) => {
    const qty = Number(prompt(`Use how many "${item.name}"? (in stock: ${item.qty_on_hand})`, "1"));
    if (!qty || qty <= 0) return;
    await api.post("/stock/use", { stock_item_id: item.id, job_id: jobId, quantity: qty, billing_method: "actual_markup", markup_pct: 25 });
    toast.success(`Used ${qty} × ${item.name} from stock`);
    setUseStock(false);
    load();
  };

  if (!job) return null;
  return (
    <section className="bg-white border border-zinc-200 rounded-2xl p-4 space-y-3" data-testid="job-links-card">
      <div className="flex items-center gap-2 text-zinc-800"><MapPin size={18} weight="bold" /><h2 className="font-heading font-bold">Property & Equipment</h2></div>

      {suggest && !job.property_id && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 flex items-center justify-between gap-2" data-testid="property-suggestion">
          <span className="text-xs text-blue-800">Matched from address: <b>{suggest.name || suggest.service_address}</b></span>
          <div className="flex gap-2 shrink-0">
            <button data-testid="link-suggested-property" onClick={() => { setProperty(suggest.id); setSuggest(null); }} className="text-xs font-bold text-blue-700 underline">Link</button>
            <button onClick={() => setSuggest(null)} className="text-xs text-zinc-400">Dismiss</button>
          </div>
        </div>
      )}

      {!newProp ? (
        <div className="flex gap-2">
          <select data-testid="job-property-select" value={job.property_id || ""} onChange={(e) => setProperty(e.target.value)}
            className="h-10 flex-1 border border-zinc-200 rounded-lg px-2 bg-white text-sm">
            <option value="">No property linked</option>
            {props.map((p) => <option key={p.id} value={p.id}>{p.name || p.service_address} {p.city ? `— ${p.city}` : ""}</option>)}
          </select>
          <Button data-testid="new-property-btn" variant="outline" size="sm" className="h-10" onClick={() => setNewProp(true)}><Plus size={16} /></Button>
        </div>
      ) : (
        <div className="space-y-2 border border-dashed border-zinc-300 rounded-lg p-2.5">
          <Input placeholder="Nickname (e.g. Main Store)" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} className="h-9" data-testid="new-property-name" />
          <Input placeholder="Service address" value={pForm.service_address} onChange={(e) => setPForm({ ...pForm, service_address: e.target.value })} className="h-9" data-testid="new-property-address" />
          <div className="grid grid-cols-3 gap-2">
            <Input placeholder="City" value={pForm.city} onChange={(e) => setPForm({ ...pForm, city: e.target.value })} className="h-9" />
            <Input placeholder="State" value={pForm.state} onChange={(e) => setPForm({ ...pForm, state: e.target.value })} className="h-9" />
            <Input placeholder="ZIP" value={pForm.zip} onChange={(e) => setPForm({ ...pForm, zip: e.target.value })} className="h-9" />
          </div>
          <div className="flex gap-2"><Button size="sm" className="h-9 flex-1" data-testid="save-property-btn" onClick={createProperty}>Save</Button>
            <Button size="sm" variant="ghost" className="h-9" onClick={() => setNewProp(false)}>Cancel</Button></div>
        </div>
      )}

      {job.property_id && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase font-bold text-zinc-400 flex items-center gap-1"><Wrench size={13} /> Equipment per task</span>
            <button data-testid="add-equipment-btn" className="text-xs font-bold text-orange-600" onClick={() => setAddEq(true)}>+ Add equipment</button>
          </div>
          {addEq && (
            <div className="space-y-2 border border-dashed border-zinc-300 rounded-lg p-2.5">
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Nickname" value={eForm.nickname} onChange={(e) => setEForm({ ...eForm, nickname: e.target.value })} className="h-9" data-testid="new-equipment-nickname" />
                <Input placeholder="Type (HVAC…)" value={eForm.equipment_type} onChange={(e) => setEForm({ ...eForm, equipment_type: e.target.value })} className="h-9" />
                <Input placeholder="Manufacturer" value={eForm.manufacturer} onChange={(e) => setEForm({ ...eForm, manufacturer: e.target.value })} className="h-9" />
                <Input placeholder="Model" value={eForm.model} onChange={(e) => setEForm({ ...eForm, model: e.target.value })} className="h-9" data-testid="new-equipment-model" />
              </div>
              <div className="flex gap-2"><Button size="sm" className="h-9 flex-1" data-testid="save-equipment-btn" onClick={createEquipment}>Save equipment</Button>
                <Button size="sm" variant="ghost" className="h-9" onClick={() => setAddEq(false)}>Cancel</Button></div>
            </div>
          )}
          {job.tasks.map((t) => {
            const sug = eqMatch(t);
            return (
              <div key={t.id} className="flex items-center gap-2">
                <span className="text-xs text-zinc-600 truncate flex-1">{t.description}</span>
                {sug && <button data-testid={`suggest-equipment-${t.id}`} onClick={() => setTaskEquip(t.id, sug.id)} className="text-[10px] font-bold text-blue-600 whitespace-nowrap">↪ {sug.nickname || sug.model}</button>}
                <select data-testid={`task-equipment-${t.id}`} value={t.equipment_id || ""} onChange={(e) => setTaskEquip(t.id, e.target.value)}
                  className="h-9 w-36 border border-zinc-200 rounded-lg px-1.5 bg-white text-xs">
                  <option value="">— no equipment —</option>
                  {equip.map((eq) => <option key={eq.id} value={eq.id}>{eq.nickname || eq.model || eq.equipment_type}</option>)}
                </select>
              </div>
            );
          })}
        </div>
      )}

      <Button data-testid="use-from-stock-btn" variant="outline" className="w-full h-10 gap-2" onClick={openStock}>
        <Package size={16} weight="bold" /> Use material from Stock
      </Button>

      <Drawer open={useStock} onOpenChange={setUseStock}>
        <DrawerContent className="max-w-md mx-auto">
          <DrawerHeader><DrawerTitle className="font-heading">Use from Stock</DrawerTitle></DrawerHeader>
          <div className="px-4 pb-2 space-y-2 max-h-[50vh] overflow-y-auto">
            <Input placeholder="Search stock…" value={stockQ} onChange={(e) => searchStock(e.target.value)} className="h-10" data-testid="stock-search-input" />
            {stockList.length === 0 && <p className="text-sm text-zinc-400 py-4 text-center">No stock items.</p>}
            {stockList.map((s) => (
              <button key={s.id} data-testid={`stock-use-${s.id}`} onClick={() => doUse(s)} disabled={s.qty_on_hand <= 0}
                className="w-full text-left border border-zinc-200 rounded-lg p-2.5 flex justify-between items-center disabled:opacity-40">
                <div><p className="font-semibold text-sm">{s.name}</p><p className="text-xs text-zinc-500">On hand: {s.qty_on_hand} · avg {money(s.avg_cost)}</p></div>
                <Plus size={18} className="text-orange-600" />
              </button>
            ))}
          </div>
          <DrawerFooter><Button variant="ghost" onClick={() => setUseStock(false)}>Close</Button></DrawerFooter>
        </DrawerContent>
      </Drawer>
    </section>
  );
};
